import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Default axios Content-Type is application/json — breaks multipart uploads (multer sees no file)
  if (config.data instanceof FormData && config.headers) {
    const h = config.headers;
    if (typeof h.delete === 'function') h.delete('Content-Type');
    else delete h['Content-Type'];
  }
  return config;
});

// Handle 401 responses (do not redirect on failed POST /auth/login — wrong password would wipe flow / reload)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = String(error.config?.url || '');
      const isLoginAttempt = url.includes('/auth/login');
      if (!isLoginAttempt) {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  /** Use right after login so the token is always sent (avoids any localStorage timing issues). */
  getMeWithToken: (token) =>
    api.get('/auth/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  getSignature: () => api.get('/auth/signature'),
  updateSignature: (data) => api.put('/auth/signature', data),
  uploadSignatureLogo: (formData) => api.post('/auth/signature-logo', formData),
  deleteSignatureLogo: () => api.delete('/auth/signature-logo')
};

// Users
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getSalesTeam: (params) => api.get('/users/sales-team', { params }),
  getInterviewZoomHosts: () => api.get('/users/interview-zoom-hosts'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

// B2B Agencies (renamed from leads module)
export const b2bAgenciesAPI = {
  getAll: (params) => api.get('/b2b-agencies', { params }),
  getById: (id) => api.get(`/b2b-agencies/${id}`),
  create: (data) => api.post('/b2b-agencies', data),
  update: (id, data) => api.put(`/b2b-agencies/${id}`, data),
  delete: (id) => api.delete(`/b2b-agencies/${id}`),
  bulkAction: (data) => api.put('/b2b-agencies/bulk/action', data)
};
// Temporary alias for older imports while flow is being migrated.
export const leadsAPI = b2bAgenciesAPI;

// Students
export const studentsAPI = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  /** While typing on student form: duplicate passport check */
  checkPassport: (params) => api.get('/students/check-passport', { params }),
  /** Country-specific duplicate checks (France: passport/email/phone, India: passport). */
  checkDuplicates: (params) => api.get('/students/check-duplicates', { params }),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  updateVisa: (id, data) => api.put(`/students/${id}/visa`, data),
  updateCompanyStatus: (id, data) => api.patch(`/students/${id}/company-status`, data),
  delete: (id) => api.delete(`/students/${id}`),
  createIntakeLink: (data) => api.post('/students/intake-links', data),
  getIntakeLinks: () => api.get('/students/intake-links'),
  deactivateIntakeLink: (id) => api.patch(`/students/intake-links/${id}/deactivate`),
  getIntakeFormMeta: (token) => api.get(`/students/intake-form/${token}/meta`),
  submitIntakeForm: (token, data) => api.post(`/students/intake-form/${token}/submit`, data),
  /** Single multipart upload (field name: file). Prefer uploadIntakeDocumentsBatch for multiple files. */
  uploadIntakeDocument: (token, formData) =>
    api.post(`/students/intake-form/${token}/upload-document`, formData),
  verifyIntakePassportDocument: (token, formData) =>
    api.post(`/students/intake-form/${token}/verify-passport-document`, formData),
  /** Multiple files in one request: fields `student_id`, `document_types` (JSON array), `files` (repeat). */
  uploadIntakeDocumentsBatch: (token, formData) =>
    api.post(`/students/intake-form/${token}/upload-documents`, formData),
  getApplicationFormPdf: (id, lang) =>
    api.get(`/students/${id}/application-form-pdf`, { params: lang ? { lang } : {}, responseType: 'blob' })
};

// Activities
export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getFollowups: (params) => api.get('/activities/followups', { params }),
  getCombined: (studentId) => api.get(`/activities/combined/${studentId}`),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  markFollowupDone: (id, source) => api.put(`/activities/${id}/mark-done`, null, { params: source ? { source } : {} }),
  delete: (id) => api.delete(`/activities/${id}`)
};

// Meetings
export const meetingsAPI = {
  getAll: (params) => api.get('/meetings', { params }),
  getCalendar: (params) => api.get('/meetings/calendar', { params }),
  getById: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  delete: (id) => api.delete(`/meetings/${id}`)
};

// Documents
export const documentsAPI = {
  getByStudent: (studentId) => api.get(`/documents/${studentId}`),
  upload: (studentId, formData) => api.post(`/documents/${studentId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  verifyPassport: (formData) => api.post('/documents/passport-verify', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (docId) => {
    // Open GCS signed URL in new tab (backend redirects to signed URL)
    const token = localStorage.getItem('crm_token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.open(`${baseUrl}/documents/download/${docId}?token=${token}`, '_blank');
  },
  getUrl: (docId) => api.get(`/documents/url/${docId}`),
  delete: (docId) => api.delete(`/documents/file/${docId}`)
};

export const companyDocumentsAPI = {
  getByCompany: (companyId) => api.get(`/documents/company/${companyId}`),
  upload: (companyId, formData) => api.post(`/documents/company/${companyId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (docId) => {
    const token = localStorage.getItem('crm_token');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.open(`${baseUrl}/documents/company-download/${docId}?token=${token}`, '_blank');
  },
  getUrl: (docId) => api.get(`/documents/company-url/${docId}`),
  delete: (docId) => api.delete(`/documents/company-file/${docId}`)
};

// Applications
export const applicationsAPI = {
  getByStudent: (studentId) => api.get(`/applications/${studentId}`),
  update: (appId, data) => api.put(`/applications/${appId}`, data)
};

// Interviews
export const interviewsAPI = {
  getAll: (params) => api.get('/interviews/all', { params }),
  getByStudent: (studentId) => api.get(`/interviews/${studentId}`),
  getById: (interviewId) => api.get(`/interviews/detail/${interviewId}`),
  getReportUrl: (interviewId) => api.get(`/interviews/file/${interviewId}/report-url`),
  uploadReport: (interviewId, formData) =>
    api.post(`/interviews/file/${interviewId}/report`, formData),
  deleteReport: (interviewId) => api.delete(`/interviews/file/${interviewId}/report`),
  create: (studentId, data) => api.post(`/interviews/${studentId}`, data),
  update: (interviewId, data) => api.put(`/interviews/${interviewId}`, data),
  delete: (interviewId) => api.delete(`/interviews/${interviewId}`)
};

// Mock Interviews
export const mockInterviewsAPI = {
  getAll: (params) => api.get('/mock-interviews/all', { params }),
  getByStudent: (studentId) => api.get(`/mock-interviews/${studentId}`),
  getById: (mockInterviewId) => api.get(`/mock-interviews/detail/${mockInterviewId}`),
  create: (studentId, data) => api.post(`/mock-interviews/${studentId}`, data),
  update: (mockInterviewId, data) => api.put(`/mock-interviews/${mockInterviewId}`, data),
  delete: (mockInterviewId) => api.delete(`/mock-interviews/${mockInterviewId}`)
};

// Zoom
export const zoomAPI = {
  getStatus: () => api.get('/zoom/status'),
  generate: (data) => api.post('/zoom/generate', data),
  getRecordings: (zoomMeetingId) => api.get(`/zoom/recordings/${zoomMeetingId}`),
  getMeetingDetails: (zoomMeetingId) => api.get(`/zoom/meeting-details/${zoomMeetingId}`),
  sync: (data) => api.post('/zoom/sync', data)
};

// Fees
export const feesAPI = {
  getAllPayments: (params) => api.get('/fees/all', { params }),
  getByStudent: (studentId, params) => api.get(`/fees/${studentId}`, { params }),
  getById: (feeId) => api.get(`/fees/detail/${feeId}`),
  create: (studentId, data) => api.post(`/fees/${studentId}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (feeId, data) => api.put(`/fees/${feeId}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  verify: (feeId, decision) => api.put(`/fees/${feeId}/verification`, { decision }),
  getReceiptUrl: (feeId) => api.get(`/fees/receipt-url/${feeId}`),
  markPaid: (feeId) => api.put(`/fees/${feeId}/mark-paid`),
  delete: (feeId) => api.delete(`/fees/${feeId}`)
};

// Refunds
export const refundsAPI = {
  getByStudent: (studentId) => api.get(`/refunds/${studentId}`),
  getAll: (params) => api.get('/refunds/all', { params }),
  getById: (refundId) => api.get(`/refunds/detail/${refundId}`),
  getDashboardStats: () => api.get('/refunds/dashboard-stats'),
  getUnreadCount: () => api.get('/refunds/unread-count'),
  create: (studentId, data) => api.post(`/refunds/${studentId}`, data),
  update: (refundId, data) => api.put(`/refunds/${refundId}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  markRead: (refundId) => api.post(`/refunds/mark-read/${refundId}`),
  markAllRead: () => api.put('/refunds/mark-all-read', {}),
  getReceiptUrl: (refundId) => api.get(`/refunds/receipt-url/${refundId}`),
  delete: (refundId) => api.delete(`/refunds/${refundId}`)
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  // Backend JSON parser is strict; never send `null` as body.
  markAllRead: (params) => api.put('/notifications/mark-all/read', {}, { params }),
  delete: (id) => api.delete(`/notifications/${id}`),
  getTicketUnreadCount: () => api.get('/notifications/ticket-unread-count'),
  markTicketRead: () => api.put('/notifications/mark-ticket-read', {}),
  getMailboxUnreadCount: () => api.get('/notifications/mailbox-unread-count'),
  markMailboxRead: () => api.put('/notifications/mark-mailbox-read', {}),
  markMailboxThreadRead: (payload) => api.put('/notifications/mark-mailbox-thread-read', payload || {})
};

// Dashboard
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getCharts: () => api.get('/dashboard/charts'),
  getRecentActivities: () => api.get('/dashboard/recent-activities'),
  getBirthdaysToday: () => api.get('/dashboard/birthdays-today'),
  getUpcomingTasks: () => api.get('/dashboard/upcoming-tasks'),
  getIndiaReports: (params) => api.get('/dashboard/india-reports', { params: params || {} }),
  getIndiaB2bAgencyReport: (params) => api.get('/dashboard/india-b2b-agency-report', { params: params || {} }),
  completeFollowup: (activityId, source) =>
    api.post(`/dashboard/complete-followup/${activityId}`, {}, source ? { params: { source } } : undefined)
};

// Agency Agreements
export const agencyAgreementsAPI = {
  getAll: (params) => api.get('/agency-agreements', { params }),
  getById: (id) => api.get(`/agency-agreements/${id}`),
  create: (data) => api.post('/agency-agreements', data),
  update: (id, data) => api.put(`/agency-agreements/${id}`, data),
  delete: (id) => api.delete(`/agency-agreements/${id}`),
  generate: (data) => api.post('/agency-agreements/generate', data),
  getGeneratedUrl: (id) => api.get(`/agency-agreements/${id}/generated-url`),
  getSignedUrl: (id) => api.get(`/agency-agreements/${id}/signed-url`),
  uploadSigned: (id, formData) => api.post(`/agency-agreements/${id}/upload-signed`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  verify: (id, decision) => api.put(`/agency-agreements/${id}/verification`, { decision })
};

// Settings
export const settingsAPI = {
  // Tied-up B2B leads (serve as agencies)
  getTiedUpAgencies: () => api.get('/settings/tied-up-agencies'),
  getCourses: (params) => api.get('/settings/courses', { params }),
  getProgramTypes: () => api.get('/settings/program_types'),
  createCourse: (data) => api.post('/settings/courses', data),
  getIntakes: () => api.get('/settings/intakes'),
  createIntake: (data) => api.post('/settings/intakes', data),
  getSources: () => api.get('/settings/sources'),
  getMeetingTypes: () => api.get('/settings/meeting_types'),
  getLeadStatuses: () => api.get('/settings/lead_statuses'),
  getStudentStatuses: () => api.get('/settings/student_statuses'),
  getVisaStatuses: () => api.get('/settings/visa_statuses'),
  getVisaResults: () => api.get('/settings/visa_results'),
  getCampuses: () => api.get('/settings/campuses'),
  getCourseCampusFees: () => api.get('/settings/course_campus_fees'),
  getDocumentTypes: (params) => api.get('/settings/document_types', { params }),
  getInternshipPrograms: () => api.get('/settings/internship_programs'),
  getStatusDesiredOptions: () => api.get('/settings/status_desired_options'),
  getHowFoundOptions: () => api.get('/settings/how_found_options'),
  getJobStudentStatuses: () => api.get('/settings/job_student_statuses'),
  getStudentCompanyStatuses: () => api.get('/settings/student_company_statuses'),
  getWebsiteLeadStatuses: () => api.get('/settings/website_lead_statuses'),
  getWebsiteLeadRoundRobin: () => api.get('/settings/website-lead-round-robin'),
  updateWebsiteLeadRoundRobin: (data) => api.put('/settings/website-lead-round-robin', data),
  getBusinessSpecializations: () => api.get('/settings/business_specializations'),
  getInterviewTimezones: () => api.get('/settings/interview_timezones'),
  getUsers: () => api.get('/users'),
  getAuditLog: (params) => api.get('/settings/audit-log', { params }),
  createMasterData: (type, data) => api.post(`/settings/${type}`, data),
  updateMasterData: (type, id, data) => api.put(`/settings/${type}/${id}`, data),
  deleteMasterData: (type, id) => api.delete(`/settings/${type}/${id}`),
  getMailboxes: () => api.get('/settings/mailboxes'),
  createMailbox: (data) => api.post('/settings/mailboxes', data),
  updateMailbox: (id, data) => api.put(`/settings/mailboxes/${id}`, data),
  deleteMailbox: (id) => api.delete(`/settings/mailboxes/${id}`),
  getSystemMailbox: () => api.get('/settings/system-mailbox'),
  updateSystemMailbox: (data) => api.put('/settings/system-mailbox', data),
  getMailSendWindow: () => api.get('/settings/mail-send-window'),
  updateMailSendWindow: (data) => api.put('/settings/mail-send-window', data),

  // Interview / Mock Interview slot capacity settings (Admin)
  getInterviewSlots: () => api.get('/settings/interview-slots'),
  createInterviewSlot: (data) => api.post('/settings/interview-slots', data),
  updateInterviewSlot: (id, data) => api.put(`/settings/interview-slots/${id}`, data),
  deleteInterviewSlot: (id) => api.delete(`/settings/interview-slots/${id}`),
  getInterviewSlotAvailability: (params) => api.get('/settings/interview-slots/availability', { params }),

  // Zoom host emails (Admin)
  getZoomHosts: () => api.get('/settings/zoom-hosts'),
  updateZoomHosts: (data) => api.put('/settings/zoom-hosts', data),

  /** Who can use student Letters tab (read GET; write PUT super_admin only) */
  getLetterGenerationAccess: () => api.get('/settings/letter-generation-access'),
  updateLetterGenerationAccess: (data) => api.put('/settings/letter-generation-access', data),

  getProspectStatuses: (params) => api.get('/settings/prospect_statuses', { params }),

  // France Job Documents (2-file config)
  getJobDocuments: () => api.get('/settings/job-documents'),
  uploadJobDocument: (formData) => api.post('/settings/job-documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteJobDocument: (id) => api.delete(`/settings/job-documents/${id}`),
  downloadJobDocumentUrl: (id) => {
    const token = localStorage.getItem('crm_token');
    const country = String(localStorage.getItem('crm_active_country') || '').toUpperCase();
    const qs = new URLSearchParams({
      token: token || '',
      ...(country ? { country } : {})
    });
    return `${API_BASE}/settings/job-documents/${id}/download?${qs.toString()}`;
  },
};

export const emailsAPI = {
  getSendWindowStatus: () => api.get('/emails/send-window-status'),
  getMailboxes: () => api.get('/emails/mailboxes'),
  searchRecipientStudents: (q) => api.get('/emails/recipients/students', { params: { q } }),
  searchRecipientAgencies: (q) => api.get('/emails/recipients/agencies', { params: { q } }),
  getThreads: (params) => api.get('/emails/threads', { params }),
  getMailboxFeed: (params) => api.get('/emails/mailbox-feed', { params }),
  getMailboxReceivedIds: () => api.get('/emails/mailbox-received-ids'),
  getEmailLogAttachments: (emailLogId) => api.get(`/emails/logs/${emailLogId}/attachments`),
  downloadEmailLogAttachment: (emailLogId, attachmentId) =>
    api.get(`/emails/logs/${emailLogId}/attachments/${encodeURIComponent(attachmentId)}/download`, {
      responseType: 'blob'
    }),
  send: (data) => api.post('/emails/send', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  reply: (data) => api.post('/emails/reply', data, { headers: { 'Content-Type': 'multipart/form-data' } })
};

export const mailAutoJobsAPI = {
  getDevStatus: () => api.get('/mail-auto-jobs/dev-status'),
  getTimezoneOptions: () => api.get('/mail-auto-jobs/timezone-options'),
  previewJob: (id, params) => api.get(`/mail-auto-jobs/${id}/preview`, { params }),
  getRunStatus: (id, params) => api.get(`/mail-auto-jobs/${id}/run-status`, { params }),
  list: () => api.get('/mail-auto-jobs'),
  get: (id) => api.get(`/mail-auto-jobs/${id}`),
  create: (data) => api.post('/mail-auto-jobs', data),
  update: (id, data) => api.put(`/mail-auto-jobs/${id}`, data),
  delete: (id) => api.delete(`/mail-auto-jobs/${id}`),
  getSendLog: (id, params) => api.get(`/mail-auto-jobs/${id}/send-log`, { params })
};

export const emailTemplatesAPI = {
  list: (params) => api.get('/email-templates', { params }),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  render: (id, data) => api.post(`/email-templates/${id}/render`, data),
  markUsed: (id, data) => api.post(`/email-templates/${id}/mark-used`, data),
  getLastUsed: (params) => api.get('/email-templates/last-used', { params })
};

// Tickets (helpdesk)
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  patch: (id, data) => api.patch(`/tickets/${id}`, data),
  getMessages: (id, params) => api.get(`/tickets/${id}/messages`, { params }),
  postMessage: (id, formData) => api.post(`/tickets/${id}/messages`, formData)
};

/** Debounced client search for tagging (students / B2B agencies) */
export const searchAPI = {
  students: (q) => api.get('/search/students', { params: { q } }),
  agencies: (q) => api.get('/search/agencies', { params: { q } })
};

// Chat
export const chatAPI = {
  getConversations: () => api.get('/chat/conversations'),
  createConversation: (data) => api.post('/chat/conversations', data),
  getMessages: (convId, params) => api.get(`/chat/conversations/${convId}/messages`, { params }),
  sendMessage: (convId, formData) => api.post(`/chat/conversations/${convId}/messages`, formData),
  updateConversation: (convId, data) => api.put(`/chat/conversations/${convId}`, data),
  addMembers: (convId, data) => api.post(`/chat/conversations/${convId}/members`, data),
  removeMember: (convId, userId) => api.delete(`/chat/conversations/${convId}/members/${userId}`),
  markRead: (convId) => api.put(`/chat/conversations/${convId}/read`),
  getUnreadTotal: () => api.get('/chat/unread-total'),
  getOnlineUsers: () => api.get('/chat/online-users'),
  getUsers: () => api.get('/chat/users')
};

// Document Templates
export const documentTemplatesAPI = {
  list: (params) => api.get('/document-templates', { params }),
  upload: (formData) => api.post('/document-templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  /** Replace .docx in GCS and re-scan placeholders (keeps template_id and settings). */
  replaceFile: (id, formData) =>
    api.put(`/document-templates/${id}/file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/document-templates/${id}`, data),
  delete: (id) => api.delete(`/document-templates/${id}`),
  getAutoFill: (params) => api.get('/document-templates/auto-fill', { params }),
  generate: (data) => api.post('/document-templates/generate', data),
  getGenerated: (params) => api.get('/document-templates/generated', { params }),
  deleteGenerated: (docId) => api.delete(`/document-templates/generated/${docId}`),
  downloadUrl: (docId) => `${API_BASE}/document-templates/download/${docId}?token=${localStorage.getItem('crm_token')}`,
  download: (docId) => api.get(`/document-templates/download/${docId}`, { responseType: 'blob' }),
  uploadSigned: (docId, formData) =>
    api.post(`/document-templates/generated/${docId}/upload-signed`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getSignedUrl: (docId) => api.get(`/document-templates/generated/${docId}/signed-url`),
  deleteSigned: (docId) => api.delete(`/document-templates/generated/${docId}/signed`)
};

// Prospects
export const prospectsAPI = {
  getAll: (params) => api.get('/prospects', { params }),
  getById: (id) => api.get(`/prospects/${id}`),
  create: (data) => api.post('/prospects', data),
  bulkAssign: (data) => api.put('/prospects/bulk-assign', data),
  update: (id, data) => api.put(`/prospects/${id}`, data),
  delete: (id) => api.delete(`/prospects/${id}`),
  getActivities: (id) => api.get(`/prospects/${id}/activities`),
  createActivity: (id, data) => api.post(`/prospects/${id}/activities`, data),
  updateActivity: (id, activityId, data) => api.put(`/prospects/${id}/activities/${activityId}`, data),
  deleteActivity: (id, activityId) => api.delete(`/prospects/${id}/activities/${activityId}`),
  convert: (id, data) => api.post(`/prospects/${id}/convert`, data),
  importCsv: (formData) => api.post('/prospects/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Companies (France)
export const companiesAPI = {
  getAll: (params) => api.get('/companies', { params }),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

// Job Openings (France)
export const jobOpeningsAPI = {
  getAll: (params) => api.get('/job-openings', { params }),
  getByStudent: (studentId) => api.get(`/job-openings/student/${studentId}`),
  getById: (id) => api.get(`/job-openings/${id}`),
  create: (data) => api.post('/job-openings', data),
  update: (id, data) => api.put(`/job-openings/${id}`, data),
  delete: (id) => api.delete(`/job-openings/${id}`),
  getStudents: (id) => api.get(`/job-openings/${id}/students`),
  getSelectableStudents: (id, params) => api.get(`/job-openings/${id}/selectable-students`, { params }),
  assignStudents: (id, data) => api.post(`/job-openings/${id}/students`, data),
  updateAssignedStudent: (id, linkId, data) => api.put(`/job-openings/${id}/students/${linkId}`, data),
  removeAssignedStudent: (id, linkId) => api.delete(`/job-openings/${id}/students/${linkId}`),
};

export default api;
